import React, { useState } from 'react';
import { flushActiveEditor } from '../../../../document-editor/flushActiveEditor';
import { FormattedMessage } from 'react-intl';
import { Button, Dropdown, OverlayTrigger, Tooltip as BsTooltip } from 'react-bootstrap';
import { useDocumentState } from '../../../../../../contexts/DocumentContext';
import { useApolloClient, useQuery } from '@apollo/client';
import { queriesV2 } from '../../../../../../api';
import { DropdownBody, DropdownToggleBlue } from '../../../../../dropdown';
import PortalDropdown from '../../../../../dropdown/PortalDropdown';
import LoadingSpinner from '../../../../../loading-spinner';
import { useDealRoles } from '../../../../../../shared/hooks/useDealRoles/useDealRoles';
import { useModals } from '../../../../../../contexts/ModalsContext';
import SendContainer from '../send/sendContainer';
import RequestInternalApprovalModalContainer from './RequestInternalApprovalModalContainer';
import ReviewViaEmailModalContainer from './modals/ReviewViaEmailModalContainer';
import { ReadyToSignContainer } from '../readyToSign';
import styles from './RequestReview.module.scss';
import { SendToPreparingToSignInternal } from '../sendToPreparingToSignInternal';
import ConfirmActionModal from '../../../../../confirm-action-modal';
import { unsendDocument } from '../../../../../../utils/api/unsend-document-offer/unsend-document';
import { INTERNAL_PREPARE_FOR_SIGNING_TOOLTIP } from '../../../../../document-table/helpers/messageConstants';
import { usePendingCounterpartySuggestions } from '../usePendingCounterpartySuggestions';

type RequestReviewContainerProps = {
  dealTeamsData: {
    [U: string]: any;
  };
  documentTeamsAdminData?: any;
  documentId: string;
  dealId: string;
  teamId: string;
  handleSaveDocument?: (() => Promise<void>) | undefined;
  isDocumentEditable: boolean;
  isDocumentCreator: boolean;
  isDocumentShared: boolean;
  shouldShowInternalSendForSigning: boolean;
  hasInviteSendingPermission: boolean;
  hasEditPermission: boolean;
  isInModal?: boolean;
  onModalClose?: () => void;
  // Status props for visibility logic
  isDraft?: boolean;
  isUploaded?: boolean;
  isReviewing?: boolean;
  isShared?: boolean;
  isExecuted?: boolean;
};

const RequestReviewContainer = ({
  dealTeamsData,
  documentTeamsAdminData,
  documentId,
  dealId,
  handleSaveDocument,
  teamId,
  isDocumentEditable,
  isDocumentCreator,
  isDocumentShared,
  shouldShowInternalSendForSigning,
  hasInviteSendingPermission,
  hasEditPermission,
  isInModal = false,
  onModalClose,
  isDraft = false,
  isUploaded = false,
  isReviewing = false,
  isShared = false,
  isExecuted = false,
}: RequestReviewContainerProps) => {
  // Use backend validation service which properly checks currentValue on placeholder links
  // This replaces the old CKEditor DOM-based check that counted ALL required placeholders as incomplete
  const { data: placeholderCompletenessData } = useQuery(queriesV2.getDocumentPlaceholderCompleteness, {
    skip: !documentId,
    fetchPolicy: 'cache-and-network',
    variables: {
      documentId: String(documentId),
      documentType: 'Document',
    },
  });

  // Query returns only INCOMPLETE placeholders, so count the required ones
  const incompletePlaceholders = placeholderCompletenessData?.getDocumentPlaceholderCompleteness?.placeholders || [];
  const placeholdersToComplete = incompletePlaceholders.filter(
    (placeholder: { isRequired: boolean }) => placeholder.isRequired
  ).length;

  const {
    showRequestInternalReviewDocumentModal: isInternalApprovalModalOpen,
    showSendDocumentModal: isSendDocumentModalOpen,
    showSendViaEmailModal: isSendViaEmailModalOpen,
    showSendForSigningPreparationModal: isSendForSigningPrepModalOpen,
    showReadyToSignModal: isReadyToSignModalOpen,
    setShowRequestInternalReviewModal,
    setShowSendDocumentModal,
    setShowSendDocumentViaEmailModal,
    setShowSendForSigningPreparationModal,
    setShowReadyToSignModal,
  } = useModals();

  const { state } = useDocumentState();
  const creatorTeamId = state.teamId;

  // Pending suggestions block "Ready to Sign" / "Send for Signing": you cannot
  // assert a document is final while changes are still proposed on it. The
  // other side has to accept or reject them first; if they reject, the document
  // comes back and whoever wants to sign re-asserts it then.
  //
  // This used to read `state.editorSuggestions`, which ONLY CKEditor ever
  // wrote. Nothing populates it under the DOCX-native editor, so it was
  // permanently `[]` — the disabled state and its tooltip never appeared and
  // the click sailed through to a mutation the server then rejected. Read the
  // relational rows the server itself gates on (kept current by
  // `useDocxSuggestionSync`), so the button and the backend agree.
  // `total` counts pending rows from EITHER party — signing is blocked by any
  // unresolved change, not just the counterparty's.
  const { total: pendingSuggestionCount } = usePendingCounterpartySuggestions(
    documentId ? String(documentId) : undefined
  );
  const hasPendingSuggestions = pendingSuggestionCount > 0;

  const [isDropdownToggled, setDropdownToggled] = useState(false);
  const [isUnsendModalOpen, setIsUnsendModalOpen] = useState(false);
  const [isUnsendLoading, setIsUnsendLoading] = useState(false);

  const { isCreator, isDealRolesLoading } = useDealRoles(dealId);
  const apolloClient = useApolloClient();

  // Helper to close dropdown when opening modals/slideouts
  const handlePortalDropdownClose = () => setDropdownToggled(false);

  const handleSendViaEmailModalClose = () => {
    setShowSendDocumentViaEmailModal(false);
  };

  const handleSendModalClose = () => {
    setShowSendDocumentModal(false);
  };

  const handleInternalApprovalModalHide = () => {
    setShowRequestInternalReviewModal(false);
  };

  const handleSendForSigningPrepModalClose = () => {
    setShowSendForSigningPreparationModal(false);
  };

  const handleReadyToSignModalClose = () => {
    setShowReadyToSignModal(false);
  };

  const handleUnsendModalClose = () => {
    setIsUnsendModalOpen(false);
  };

  const handleUnsendConfirm = async () => {
    setIsUnsendLoading(true);
    try {
      await unsendDocument({ documentId, dealId })(apolloClient);
      // Reset Apollo store to force all queries to refetch with updated data
      // This ensures banner and status pill update immediately
      await apolloClient.resetStore();
      // Reload page to ensure UI reflects updated document status
      window.location.reload();
    } catch (error) {
      // Silently fail and reset loading state
      setIsUnsendLoading(false);
    }
    // Note: finally block removed since page reload prevents it from executing
  };

  if (isDealRolesLoading || !dealTeamsData) {
    return <LoadingSpinner />;
  }

  const {
    getDealById: {
      members: dealMembers,
      team: { approvers: creatorTeamApprovers },
    },
  } = dealTeamsData;

  const receiverTeamApprovers = dealTeamsData?.getDealById?.receiverTeam?.approvers;

  const creatorTeamAdmin = documentTeamsAdminData?.getDocumentById?.team?.adminUser;
  let receiverTeamAdmin = documentTeamsAdminData?.getDocumentById?.receiverTeam?.adminUser;

  if (!receiverTeamAdmin) {
    receiverTeamAdmin =
      teamId === dealTeamsData?.getDealById?.receiverTeam?.id
        ? creatorTeamAdmin
        : dealTeamsData?.getDealById?.receiverTeam?.adminUser;
  }

  const counterpartyEmail = teamId === creatorTeamId ? receiverTeamAdmin?.email : creatorTeamAdmin?.email;
  const counterpartyName = teamId === creatorTeamId ? receiverTeamAdmin?.fullName : creatorTeamAdmin?.fullName;

  // Determine if current user is on the original DEAL creator team (not just document creator)
  const dealCreatorTeamId = dealTeamsData?.getDealById?.team?.id;
  const isUserDealCreator = teamId === dealCreatorTeamId;

  const showInternalSendForSigningPrep = shouldShowInternalSendForSigning || isSendForSigningPrepModalOpen;

  // Check if document has receiver team (for Unsend visibility on executed docs)
  // Use document-level receiver team, not deal-level, since documents can have their own receiver team
  const receiverTeam = documentTeamsAdminData?.getDocumentById?.receiverTeam;
  const hasReceiverTeam = !!receiverTeam;

  // Dropdown item visibility logic based on user's requirements
  const showSendToCounterparty =
    isDocumentCreator
      ? hasInviteSendingPermission && (isDraft || isReviewing || isUploaded || isShared || (isExecuted && hasReceiverTeam))
      : hasEditPermission && isReviewing;

  const showShareViaEmail =
    isDocumentCreator
      ? hasInviteSendingPermission && (isDraft || isUploaded || isReviewing || isShared)
      : hasEditPermission && (isReviewing || isShared);

  const showInternalReview =
    hasEditPermission && isDocumentEditable;

  const showReadyToSign = shouldShowInternalSendForSigning ||
    (!isDocumentCreator && isReviewing && hasEditPermission);

  const showUnsend =
    isDocumentCreator && hasInviteSendingPermission && (isReviewing || isShared || (isExecuted && hasReceiverTeam));

  // Dynamic label for Send button: "Send to" vs "Counterparty Review" vs "Back to"
  const sendLabel = isShared
    ? 'Counterparty Review'
    : (isDraft || isUploaded)
      ? 'Send to Counterparty'
      : 'Back to Counterparty';

  // Dynamic label for signing: Creator sees "Send for Signing", Counterparty sees "Ready to Sign"
  const signingLabel = isDocumentCreator ? 'Send for Signing' : 'Ready to Sign';

  // Use PortalDropdown when inside a modal, regular Dropdown otherwise
  const DropdownComponent = isInModal ? PortalDropdown : Dropdown;

  return (
    <div className={styles.requestReviewContainer}>
      <div className={styles.requestReviewDropdown}>
        <DropdownComponent
          show={isDropdownToggled}
          onToggle={(nextShow: boolean) => {
            setDropdownToggled(nextShow);
            // Land in-flight edits when the actions dropdown opens, so every
            // action behind it (send, ready-to-sign, request review) sees the
            // current body and an accurate pending-suggestion count. Was a
            // CKEditor-only `window.editor` call that no-oped under docx-native.
            if (nextShow) void flushActiveEditor(documentId);
          }}
          className={styles.requestReviewDropdown}
        >
          <Dropdown.Toggle id="dropdown-deal-actions" as={DropdownToggleBlue}>
            <span className={styles.dropdownTitle}>
              <FormattedMessage id="deals.actions" defaultMessage="Send" />
            </span>
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <div className={styles.sendDropdownBodyWrapper}>
              <DropdownBody>
                {/* Send to/Back to Counterparty */}
                {showSendToCounterparty && (
                  <Dropdown.Item
                    as={Button}
                    variant="link"
                    onClick={() => {
                      if (onModalClose) onModalClose();
                      setShowSendDocumentModal(true);
                    }}
                  >
                    {sendLabel}
                  </Dropdown.Item>
                )}

                {/* Share via Email */}
                {showShareViaEmail && (
                  <Dropdown.Item
                    as={Button}
                    variant="link"
                    onClick={() => {
                      if (onModalClose) onModalClose();
                      setShowSendDocumentViaEmailModal(true);
                    }}
                  >
                    Share via Email
                  </Dropdown.Item>
                )}

                {/* Internal Review */}
                {showInternalReview && (
                  <Dropdown.Item
                    as={Button}
                    variant="link"
                    onClick={() => {
                      if (onModalClose) onModalClose();
                      setShowRequestInternalReviewModal(true);
                    }}
                  >
                    Internal Review
                  </Dropdown.Item>
                )}

                {/* Ready to Sign (Internal) */}
                {showReadyToSign && (
                  <Dropdown.Item
                    as={Button}
                    className={styles.signInButton}
                    variant="link"
                    disabled={hasPendingSuggestions}
                    onClick={() => {
                      if (!hasPendingSuggestions) {
                        if (onModalClose) onModalClose();
                        if (isDocumentCreator) {
                          setShowSendForSigningPreparationModal(true);
                        } else {
                          setShowReadyToSignModal(true);
                        }
                      }
                    }}
                  >
                    {signingLabel}
                    {hasPendingSuggestions && (
                      <OverlayTrigger
                        placement="auto"
                        flip={true}
                        overlay={
                          <BsTooltip id="signing-pending-suggestions-tooltip" className={styles.signingTooltip}>
                            {INTERNAL_PREPARE_FOR_SIGNING_TOOLTIP}
                          </BsTooltip>
                        }
                      >
                        <span className={styles.infoIcon}>!</span>
                      </OverlayTrigger>
                    )}
                  </Dropdown.Item>
                )}

                {/* Unsend - Destructive action with enhanced visual separation */}
                {showUnsend && (
                  <Dropdown.Item
                    as={Button}
                    variant="link"
                    className={styles.unsendAction}
                    onClick={() => {
                      if (onModalClose) onModalClose();
                      setIsUnsendModalOpen(true);
                    }}
                  >
                    Unshare
                  </Dropdown.Item>
                )}
              </DropdownBody>
            </div>
          </Dropdown.Menu>
        </DropdownComponent>
      </div>

      <>
        <RequestInternalApprovalModalContainer
            isOpen={isInternalApprovalModalOpen}
            placeholdersToComplete={placeholdersToComplete}
            onClose={handleInternalApprovalModalHide}
            dealMembers={dealMembers}
            teamMembersWithApprovePermission={isCreator ? creatorTeamApprovers : receiverTeamApprovers}
            documentId={documentId}
            handleSaveDocument={handleSaveDocument}
            teamId={teamId}
            isReviewOnly={isDocumentShared && !isDocumentCreator}
          />

          <ReviewViaEmailModalContainer
            isOpen={isSendViaEmailModalOpen}
            onClose={handleSendViaEmailModalClose}
            placeholdersToComplete={placeholdersToComplete}
            teamId={teamId}
            dealId={dealId}
          />

          <SendContainer
            isOpen={isSendDocumentModalOpen}
            onClose={handleSendModalClose}
            teamId={teamId}
            documentId={documentId}
            dealId={dealId}
            showCounterparty={true}
            counterpartyEmail={counterpartyEmail}
            counterpartyName={counterpartyName}
            handleSaveDocument={handleSaveDocument}
            placeholdersToComplete={placeholdersToComplete}
            isUserDealCreator={isUserDealCreator}
          />

          <SendToPreparingToSignInternal
            isOpen={isSendForSigningPrepModalOpen}
            onClose={handleSendForSigningPrepModalClose}
            dealId={dealId}
            documentId={documentId}
            teamId={teamId}
            handleSaveDocument={handleSaveDocument}
            placeholdersToComplete={placeholdersToComplete}
          />

          {!isDocumentCreator && (
            <ReadyToSignContainer
              isOpen={isReadyToSignModalOpen}
              onClose={handleReadyToSignModalClose}
              documentId={documentId}
              dealId={dealId}
              teamId={teamId}
              creatorTeamName={dealTeamsData?.getDealById?.team?.name}
            />
          )}

          <ConfirmActionModal
            show={isUnsendModalOpen}
            onHide={handleUnsendModalClose}
            onClick={handleUnsendConfirm}
            title={<FormattedMessage id="unsend-document.modal.title" defaultMessage="Unshare Document" />}
            message={
              <FormattedMessage
                id="unsend-document.modal.message"
                defaultMessage="Are you sure you want to unshare this document? The counterparty will lose access and the document will return to your possession."
              />
            }
            action={
              isUnsendLoading ? (
                <FormattedMessage id="unsend-document.modal.action.loading" defaultMessage="Unsharing..." />
              ) : (
                <FormattedMessage id="unsend-document.modal.action" defaultMessage="Unshare" />
              )
            }
          />
      </>
    </div>
  );
};

export default RequestReviewContainer;
